import { useQuery } from "@tanstack/react-query";
import { ErrorMessage, Field, Form, Formik, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import {
	Navigate,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { KTCard, KTCardBody, KTIcon } from "../../../_metronic/helpers";
import { PageTitle } from "../../../_metronic/layout/core";
import { useAuth } from "../../modules/auth";
import { useBlocksByDistrict } from "../block/hooks/useBlock";
import { useDistricts } from "../district/hooks/useDistrict";
import { FileUpload } from "../registration/helper/FileUpload";
import { useVillages } from "../registration/hooks/useMasterData";
import {
	fetchDistrictEmployeeByCode,
	fetchVillageEmployeeByMobile,
	uploadSchemeReport,
} from "./api/SchemeDataEntry.api";
import { fetchSchemeEntriesForReport } from "../schemeDataEntryReport/api/SchemeDataEntryReport.api";
import BlockDistrictAssemblyForm from "./components/BlockDistrictAssemblyForm/BlockDistrictAssemblyForm";
import HLCForm from "./components/HLCForm/HLCForm";
import CMBFSSchemeStepper from "./components/CMBFSForm/CMBFSSchemeStepper";
import DsjeSubSchemeForm from "./components/DsjeForm/DsjeSubSchemeForm";
import { getDsjeSubSchemeOrder } from "./components/DsjeForm/dsjeSubSchemeFormConfig";
import { IssueCascadeFields } from "./components/IssueCascadeFields";
import { useSchemeIssueMappingTree } from "../schemeIssueMapping/hooks/useSchemeIssueMapping";
import NMPSchemeStepper from "./components/NMPForm/NMPSchemeStepper";
import {
	useAuditYears,
	useEmployeeCodesByRoleId,
	useExpenditureYears,
	useLatestAuditCalendar,
	useMGNREGSById,
	useSchemeSubmit,
	useSchemesList,
	useSchemeUpdate,
	useSubSchemesForScheme,
	useUploadSchemeReport,
	useHlcRegistrationNumbers,
} from "./hooks/useSchemeDataEntry";
import {
	MGNREGSFormData,
	ParaIssue,
	PMAYGFormData,
	PMAYGParaIssue,
	VRPDetail,
} from "./types/SchemeDataEntry.type";

const parseJsonField = (field: any) => {
	if (typeof field === "string") {
		try {
			return JSON.parse(field);
		} catch (e) {
			console.error("Failed to parse JSON field:", e);
			return [];
		}
	}
	return Array.isArray(field) ? field : [];
};

const getMinGramSabhaDate = (auditEndDate: string) => {
	if (!auditEndDate) return undefined;
	const d = new Date(auditEndDate);
	if (isNaN(d.getTime())) return undefined;
	d.setDate(d.getDate() + 1);
	return d.toISOString().split("T")[0];
};

const MGNREGSFormSchema = Yup.object().shape({
	// Section A
	brp_employee_code: Yup.string().required("Required"),
	// Section B
	district_id: Yup.number().required("Required"),
	block_id: Yup.number().required("Required"),
	village_id: Yup.number().required("Required"),
	// Text, not a number: audit_calendars.round_no can hold non-numeric pilot
	// labels (e.g. "Pilot 2"), not just plain round numbers.
	round_no: Yup.string().required("Required"),
	audit_start_date: Yup.string().required("Audit Start Date is required"),
	audit_end_date: Yup.string()
		.required("Audit End Date is required")
		.test(
			"is-after-start",
			"Audit End Date must be on or after Audit Start Date",
			function (value) {
				const { audit_start_date } = this.parent;
				if (!audit_start_date || !value) return true;
				return new Date(value) >= new Date(audit_start_date);
			},
		),
	sgs_date: Yup.string()
		.required("Gram Sabha Date is required")
		.test(
			"is-after-end",
			"Gram Sabha Date must be after Audit End Date",
			function (value) {
				const { audit_end_date } = this.parent;
				if (!audit_end_date || !value) return true;
				return new Date(value) > new Date(audit_end_date);
			},
		),
	sa_period_from_date: Yup.string().required("SA Period From Date is required"),
	sa_period_to_date: Yup.string()
		.required("SA Period To Date is required")
		.test(
			"is-after-sa-from",
			"SA Period To Date must be on or after SA Period From Date",
			function (value) {
				const { sa_period_from_date } = this.parent;
				if (!sa_period_from_date || !value) return true;
				return new Date(value) >= new Date(sa_period_from_date);
			},
		),
	expenditure_year_id: Yup.number().nullable(),
	audit_year_id: Yup.number().required("Required"),
	// Section D
	total_works: Yup.number().required("Required"),
	// total_expenditure_amount is calculated
	// Section F
	photo_1: Yup.mixed().required("Audit Photo 1 is compulsory"),
});

const ParaIssueRow = ({
	index,
	isView,
	isEdit,
	isAdmin,
	issue,
	setFieldValue,
	remove,
	schemeId,
	hlcOptions = [],
}: {
	index: number;
	isView: boolean;
	isEdit?: boolean;
	isAdmin?: boolean;
	issue: any;
	setFieldValue: any;
	remove: any;
	schemeId?: number | null;
	hlcOptions?: string[];
}) => {
	// Once data update/saved, Issue No to Recovered Amount are editable ONLY by admin (role_id 1)
	const isCoreDisabled =
		isView ||
		(Boolean(isEdit) &&
			!isAdmin &&
			Boolean(
				issue?.issue_no ||
					issue?.code_no ||
					(issue?.amount !== null && issue?.amount !== undefined) ||
					(issue?.no_of_cases !== null && issue?.no_of_cases !== undefined) ||
					issue?.issue_type_id ||
					issue?.issue_category_id,
			));

	// Once an issue has been saved as Closed, its NREGS Issue No / Status /
	// HLC fields / Reason are frozen for non-admins. Uses the saved (initial)
	// status, not the live form value, so picking "Closed" doesn't lock the
	// row before it's saved.
	const { initialValues } = useFormikContext<{ issues?: ParaIssue[] }>();
	const isClosedLocked =
		isView ||
		(Boolean(isEdit) &&
			!isAdmin &&
			initialValues?.issues?.[index]?.status === "Closed");

	// Make sure current issue's hlc_reg_no is included in options if present
	const optionsList = Array.from(
		new Set(
			[...hlcOptions, issue?.hlc_reg_no].filter(
				(opt) => Boolean(opt) && typeof opt === "string",
			),
		),
	);

	// The two Grievance-specific fields only apply when the selected issue
	// Type's name is "Grievances" — looked up from the same mapping tree
	// IssueCascadeFields uses (cached by schemeId, so this is a free re-read).
	const { tree } = useSchemeIssueMappingTree(schemeId);
	const selectedTypeName = tree.types.find(
		(t) => Number(t.id) === Number(issue?.issue_type_id),
	)?.name;
	const isGrievanceType =
		String(selectedTypeName || "").trim().toLowerCase() === "grievances";

	return (
		<div className="border rounded p-5 mb-5 bg-light-lighten shadow-sm">
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Issue No</label>
					<Field
						name={`issues.${index}.issue_no`}
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">NREGS Issue No</label>
					<Field
						name={`issues.${index}.nregs_issue_no`}
						value={issue?.nregs_issue_no ?? ""}
						className="form-control form-control-solid"
						disabled={isClosedLocked}
					/>
				</div>
				<IssueCascadeFields
					index={index}
					isView={isView}
					disabled={isCoreDisabled}
					issue={issue}
					setFieldValue={setFieldValue}
					schemeId={schemeId}
					typeColClass="col-md-3 fv-row"
					categoryColClass="col-md-3 fv-row"
					descriptionFieldName="description"
				/>
			</div>
			<div className="mb-5 fv-row">
				<label className="form-label fw-bold">
					Description
				</label>
				<Field
					as="textarea"
					name={`issues.${index}.description`}
					value={issue?.description ?? ""}
					className="form-control form-control-solid"
					rows={3}
					maxLength={1000}
					disabled={isCoreDisabled}
				/>
			</div>
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Code No.</label>
					<Field
						name={`issues.${index}.code_no`}
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">No. of Cases</label>
					<Field
						name={`issues.${index}.no_of_cases`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
						onKeyDown={blockInvalidNumberKeys}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Amount</label>
					<Field
						name={`issues.${index}.amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
						onKeyDown={blockInvalidNumberKeys}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Gram Sabha Recovered Amount</label>
					<Field
						name={`issues.${index}.recovered_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
						onKeyDown={blockInvalidNumberKeys}
					/>
				</div>
			</div>
			{isGrievanceType && (
				<div className="row g-9 mb-5">
					<div className="col-md-6 fv-row">
						<label className="form-label fw-bold">
							Grievance (Total No. of Individuals)
						</label>
						<Field
							name={`issues.${index}.grievance_total_individuals`}
							type="number"
							className="form-control form-control-solid"
							disabled={isCoreDisabled}
							onKeyDown={blockInvalidNumberKeys}
						/>
					</div>
					<div className="col-md-6 fv-row">
						<label className="form-label fw-bold">
							Number of Individuals Whose Grievance Was Redressed
						</label>
						<Field
							name={`issues.${index}.grievance_redressed_individuals`}
							type="number"
							className="form-control form-control-solid"
							disabled={isCoreDisabled}
							onKeyDown={blockInvalidNumberKeys}
						/>
					</div>
				</div>
			)}
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">HLC Reg No.</label>
					<Field
						as="select"
						name={`issues.${index}.hlc_reg_no`}
						className="form-select form-select-solid"
						disabled={isClosedLocked}
					>
						<option value="">Select HLC No.</option>
						{optionsList.map((opt: string) => (
							<option key={opt} value={opt}>
								{opt}
							</option>
						))}
					</Field>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Status</label>
					<Field
						as="select"
						name={`issues.${index}.status`}
						className="form-select form-select-solid"
						disabled={isClosedLocked}
					>
						<option value="Pending">Pending</option>
						<option value="Closed">Closed</option>
					</Field>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">HLC Recovery Amount</label>
					<Field
						name={`issues.${index}.hlc_recovery_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isClosedLocked}
					/>
				</div>
				{!isView && isAdmin && (
					<div className="col-md-3 fv-row d-flex align-items-end justify-content-end">
						<button
							type="button"
							className="btn btn-icon btn-danger me-2"
							onClick={remove}
						>
							<KTIcon iconName="trash" className="fs-2" />
						</button>
						<button
							type="button"
							className="btn btn-light"
							onClick={() => {
								setFieldValue(`issues.${index}`, {
									issue_no: "",
									issue_type_id: null,
									issue_category_id: null,
									issue_sub_category_id: null,
									code_no: "",
									description: "",
									no_of_cases: null,
									amount: null,
									recovered_amount: null,
									hlc_reg_no: "",
									status: "Pending",
									hlc_recovery_amount: null,
									nregs_issue_no: "",
									grievance_total_individuals: null,
									grievance_redressed_individuals: null,
									status_reason: "",
								});
							}}
						>
							Clear
						</button>
					</div>
				)}
			</div>
			{issue?.status === "Closed" && (
				<div className="fv-row">
					<label className="form-label fw-bold">
						Reason (Max 1000 chars)
					</label>
					<Field
						as="textarea"
						name={`issues.${index}.status_reason`}
						value={issue?.status_reason ?? ""}
						className="form-control form-control-solid"
						rows={3}
						maxLength={1000}
						disabled={isClosedLocked}
					/>
				</div>
			)}
		</div>
	);
};

const PMAYGParaIssueRow = ({
	index,
	isView,
	isEdit,
	isAdmin,
	issue,
	setFieldValue,
	remove,
	schemeId,
	hlcOptions = [],
}: {
	index: number;
	isView: boolean;
	isEdit?: boolean;
	isAdmin?: boolean;
	issue: any;
	setFieldValue: any;
	remove: any;
	schemeId?: number | null;
	hlcOptions?: string[];
}) => {
	// On the Edit screen, Issue No through No. of Grievances are editable
	// only by admin (role_id 1) — other roles get a read-only view of what
	// was originally entered.
	const isCoreDisabled = isView || (Boolean(isEdit) && !isAdmin);

	// Make sure current issue's hlc_reg_no is included in options if present
	const optionsList = Array.from(
		new Set(
			[...hlcOptions, issue?.hlc_reg_no].filter(
				(opt) => Boolean(opt) && typeof opt === "string",
			),
		),
	);

	return (
		<div className="border rounded p-5 mb-5 bg-light-lighten shadow-sm">
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Issue No.</label>
					<Field
						name={`issues.${index}.issue_no`}
						className="form-control form-control-solid"
						disabled={true}
					/>
				</div>
				<IssueCascadeFields
					index={index}
					isView={isView}
					disabled={isCoreDisabled}
					issue={issue}
					setFieldValue={setFieldValue}
					schemeId={schemeId}
					typeColClass="col-md-3 fv-row"
					categoryColClass="col-md-6 fv-row"
					descriptionFieldName="description"
				/>
			</div>
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Code No.</label>
					<Field
						name={`issues.${index}.code_no`}
						className="form-control form-control-solid"
						disabled={true}
					/>
				</div>
			</div>
			<div className="mb-5 fv-row">
				<label className="form-label fw-bold required">
					Description
				</label>
				<Field
					as="textarea"
					name={`issues.${index}.description`}
					className="form-control form-control-solid"
					rows={3}
					disabled={isCoreDisabled}
				/>
			</div>
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">No. of Beneficiaries</label>
					<Field
						name={`issues.${index}.no_of_beneficiaries`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Central Amount</label>
					<Field
						name={`issues.${index}.central_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">State Amount</label>
					<Field
						name={`issues.${index}.state_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Others Amount</label>
					<Field
						name={`issues.${index}.others_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
			</div>
			<div className="row g-9 mb-5">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">No. of Grievances</label>
					<Field
						name={`issues.${index}.no_of_grievances`}
						type="number"
						className="form-control form-control-solid"
						disabled={isCoreDisabled}
					/>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">HLC Reg No.</label>
					<Field
						as="select"
						name={`issues.${index}.hlc_reg_no`}
						className="form-select form-select-solid"
						disabled={isView}
					>
						<option value="">Select HLC No.</option>
						{optionsList.map((opt: string) => (
							<option key={opt} value={opt}>
								{opt}
							</option>
						))}
					</Field>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Status</label>
					<Field
						as="select"
						name={`issues.${index}.status`}
						className="form-select form-select-solid"
						disabled={isView}
					>
						<option value="Pending">Pending</option>
						<option value="Closed">Closed</option>
					</Field>
				</div>
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">Recovery Amount</label>
					<Field
						name={`issues.${index}.recovery_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isView}
					/>
				</div>
			</div>
			<div className="row g-9 mb-3">
				<div className="col-md-3 fv-row">
					<label className="form-label fw-bold">HLC Recovery Amount</label>
					<Field
						name={`issues.${index}.hlc_recovery_amount`}
						type="number"
						className="form-control form-control-solid"
						disabled={isView}
					/>
				</div>
				{!isView && isAdmin && (
					<div className="col-md-9 d-flex align-items-end justify-content-end">
						<button
							type="button"
							className="btn btn-icon btn-light-danger me-2"
							onClick={remove}
						>
							<KTIcon iconName="trash" className="fs-2" />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

const blockInvalidNumberKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (["e", "E", "+", "-"].includes(e.key)) {
		e.preventDefault();
	}
};

const caseStudyOptions = [
	"Para",
	"Positive Aspect",
	"Innovative practice",
	"Audit process",
	"impact of the audit",
	"others",
];

const SyncFormValues = ({
	setFieldValue,
	values,
	currentUser,
	initialData,
	districts,
	brpBlocks,
	blocks,
	setBrpSelectedDistrict,
	setSelectedDistrict,
	setSelectedBlock,
	auditCalendarDefaults,
	brpPresentLocation,
	auditYears,
}: any) => {
	// Auto-fill the derived Section B fields (Round No/Audit Start-End
	// Date/SA Period From-To Date/Audit Year) from the audit calendar entry
	// matching the scheme + the District/Block/Panchayat/Gram Sabha Date the
	// user has already picked. District/Block/Panchayat/LGD Code/Gram Sabha
	// Date stay fully user-selected — they are the lookup key, not the
	// output — only on a brand new entry.
	useEffect(() => {
		if (!initialData && auditCalendarDefaults) {
			setFieldValue("round_no", auditCalendarDefaults.round_no);
			setFieldValue("audit_start_date", auditCalendarDefaults.start_date);
			setFieldValue("audit_end_date", auditCalendarDefaults.end_date);
			setFieldValue(
				"sa_period_from_date",
				auditCalendarDefaults.sa_period_from_date,
			);
			setFieldValue(
				"sa_period_to_date",
				auditCalendarDefaults.sa_period_to_date,
			);
			// audit_calendars stores the financial year as a plain string
			// (e.g. "2026-2027"), while this form's Audit Year is a select
			// keyed by the audit_years master table's id — match by name.
			const matchedYear = auditYears?.find(
				(y: any) => y.name === auditCalendarDefaults.financial_year,
			);
			if (matchedYear) {
				setFieldValue("audit_year_id", matchedYear.id);
			}
		}
	}, [initialData, auditCalendarDefaults, setFieldValue, auditYears]);

	// Sync BRP Details (Section A) Employee Code/Name/Mobile — always from
	// the logged-in user (or the saved entry, when editing/viewing).
	useEffect(() => {
		if (currentUser || initialData) {
			const empCode =
				initialData?.brp_employee_code || currentUser?.employee_code || "";
			const name = initialData?.brp_name || currentUser?.name || "";
			const mobile = initialData?.brp_mobile_no || currentUser?.mobile || "";

			if (empCode && values.brp_employee_code !== empCode) {
				setFieldValue("brp_employee_code", empCode);
			}
			if (name && values.brp_name !== name) {
				setFieldValue("brp_name", name);
			}
			if (mobile && values.brp_mobile_no !== mobile) {
				setFieldValue("brp_mobile_no", mobile);
			}
		}
	}, [currentUser, initialData, setFieldValue]);

	// Sync BRP Details (Section A) "Present District" — on a brand-new
	// entry, this comes ONLY from the logged-in BRP's currently ongoing
	// (Present) posting in Staff Registration Working Details, and is left
	// empty if they haven't marked one yet (never falls back to their
	// home/registered district). On an existing saved entry, keep whatever
	// was recorded at submission time.
	useEffect(() => {
		if (initialData) {
			const rawBrpDistId = Number(initialData?.brp_district_id) || null;
			const brpDistName = initialData?.brp_district_name;

			let matchedBrpDist = rawBrpDistId
				? districts?.find((d: any) => Number(d.id) === rawBrpDistId)
				: null;
			if (!matchedBrpDist && !rawBrpDistId && brpDistName) {
				matchedBrpDist = districts?.find(
					(d: any) =>
						typeof brpDistName === "string" &&
						d.name?.toLowerCase() === brpDistName.toLowerCase(),
				);
			}
			const brpDistId = matchedBrpDist
				? Number(matchedBrpDist.id)
				: rawBrpDistId;

			if (brpDistId && Number(values.brp_district_id) !== brpDistId) {
				setFieldValue("brp_district_id", brpDistId);
				setBrpSelectedDistrict(brpDistId);
			}
		} else if (
			brpPresentLocation?.present_district_id &&
			Number(values.brp_district_id) !==
				Number(brpPresentLocation.present_district_id)
		) {
			setFieldValue(
				"brp_district_id",
				Number(brpPresentLocation.present_district_id),
			);
			setBrpSelectedDistrict(Number(brpPresentLocation.present_district_id));
		}
	}, [
		initialData,
		brpPresentLocation,
		districts,
		setFieldValue,
		setBrpSelectedDistrict,
	]);

	// Sync BRP Block (Section A) "Present Block" as soon as brpBlocks
	// finishes loading — same source rule as Present District above.
	useEffect(() => {
		if (!brpBlocks || brpBlocks.length === 0) return;

		if (initialData) {
			const rawBrpBlkId = Number(initialData?.brp_block_id) || null;
			const brpBlkName = initialData?.brp_block_name;

			let matchedBrpBlk = rawBrpBlkId
				? brpBlocks.find((b: any) => Number(b.id) === rawBrpBlkId)
				: null;
			if (!matchedBrpBlk && brpBlkName) {
				matchedBrpBlk = brpBlocks.find(
					(b: any) =>
						typeof brpBlkName === "string" &&
						b.name?.toLowerCase() === brpBlkName.toLowerCase(),
				);
			}
			const brpBlkId = matchedBrpBlk ? Number(matchedBrpBlk.id) : rawBrpBlkId;

			if (brpBlkId) {
				setFieldValue("brp_block_id", brpBlkId);
			}
		} else if (brpPresentLocation?.present_block_id) {
			setFieldValue(
				"brp_block_id",
				Number(brpPresentLocation.present_block_id),
			);
		}
	}, [initialData, brpPresentLocation, brpBlocks, setFieldValue]);

	// Sync Basic Details District (Section B) - ONLY when editing existing initialData
	useEffect(() => {
		if (initialData) {
			const rawDistId = Number(initialData?.district_id);
			const distName = initialData?.district_name;

			let matchedDist = rawDistId
				? districts?.find((d: any) => Number(d.id) === rawDistId)
				: null;
			if (!matchedDist && distName) {
				matchedDist = districts?.find(
					(d: any) =>
						typeof distName === "string" &&
						d.name?.toLowerCase() === distName.toLowerCase(),
				);
			}
			const distId = matchedDist ? Number(matchedDist.id) : rawDistId;

			if (
				distId &&
				(Number(values.district_id) !== distId || !values.district_id)
			) {
				setFieldValue("district_id", distId);
				setSelectedDistrict(distId);
			}
		}
	}, [initialData, districts, setFieldValue, setSelectedDistrict]);

	// Sync Basic Details Block (Section B) as soon as blocks finish loading - ONLY when editing existing initialData
	useEffect(() => {
		if (initialData && blocks && blocks.length > 0) {
			const rawBlkId = Number(initialData?.block_id);
			const blkName = initialData?.block_name;

			let matchedBlk = rawBlkId
				? blocks.find((b: any) => Number(b.id) === rawBlkId)
				: null;
			if (!matchedBlk && blkName) {
				matchedBlk = blocks.find(
					(b: any) =>
						typeof blkName === "string" &&
						b.name?.toLowerCase() === blkName.toLowerCase(),
				);
			}
			const blkId = matchedBlk ? Number(matchedBlk.id) : rawBlkId;

			if (blkId && (Number(values.block_id) !== blkId || !values.block_id)) {
				setFieldValue("block_id", blkId);
				setSelectedBlock(blkId);
			}
		}
	}, [initialData, blocks, setFieldValue, setSelectedBlock]);

	return null;
};

const SchemeDataEntryFormPage = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isEdit = !!id && location.pathname.includes("/edit/");
	const isView = !!id && location.pathname.includes("/view/");

	const { currentUser } = useAuth() as any;
	const userRoleId = Number(currentUser?.role_id);
	const canAdd = userRoleId === 4;
	const canEdit = [1, 4].includes(userRoleId);
	const canView = [1, 2, 3, 4, 6].includes(userRoleId);

	if (isView && !canView) return <Navigate to="/dashboard" />;
	if (isEdit && !canEdit) return <Navigate to="/dashboard" />;
	if (!id && !canAdd) return <Navigate to="/dashboard" />;

	const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
	const [selectedSubSchemeId, setSelectedSubSchemeId] = useState<number | null>(
		null,
	);
	const { data: schemes, isLoading: isSchemesLoading } = useSchemesList();
	const { subSchemes, isLoading: isSubSchemesLoading } =
		useSubSchemesForScheme(selectedSchemeId);
	const submitMutation = useSchemeSubmit();
	const updateMutation = useSchemeUpdate();
	const { data: initialDataRaw, isLoading: isInitialLoading } =
		useMGNREGSById(id);
	let parsedJsonData = initialDataRaw?.json_data;
	if (typeof parsedJsonData === "string") {
		try {
			parsedJsonData = JSON.parse(parsedJsonData);
		} catch (e) {
			console.error("Failed to parse json_data:", e);
			parsedJsonData = {};
		}
	}
	const initialData = parsedJsonData
		? { ...initialDataRaw, ...parsedJsonData }
		: initialDataRaw;
	const uploadMutation = useUploadSchemeReport();
	const { districts } = useDistricts(1, 1000);
	const [selectedDistrict, setSelectedDistrict] = useState<number | null>(
		initialData?.district_id ? Number(initialData.district_id) : null,
	);
	const [selectedBlock, setSelectedBlock] = useState<number | null>(
		initialData?.block_id ? Number(initialData.block_id) : null,
	);

	useEffect(() => {
		if (initialData) {
			setSelectedDistrict(Number(initialData.district_id) || null);
			setSelectedBlock(Number(initialData.block_id) || null);
			setSelectedDrpRoleId(Number(initialData.drp_role_id) || null);
			setSelectedSchemeId(Number(initialData.scheme_id) || null);
			setSelectedSubSchemeId(Number(initialData.sub_scheme_id) || null);
		}
	}, [initialData]);

	const handleSelectScheme = (schemeId: number) => {
		setSelectedSchemeId(schemeId);
		setSelectedSubSchemeId(null);
	};

	const { data: blocksData } = useBlocksByDistrict(selectedDistrict || 0);
	const blocks = blocksData || [];

	const { data: villagesData } = useVillages(selectedBlock || 0);
	const villages = villagesData || [];

	// Whether this scheme's Section B: Basic Details is backed by the audit
	// calendar (MGNREGS/VB G RAM G or PMAY-G) — the actual lookup runs
	// inside each scheme's own Formik render-prop scope below, keyed by the
	// user's own District/Block/Panchayat/Gram Sabha Date selection (not
	// just district), since a district can have several audit calendar
	// entries, one per Panchayat.
	const currentUserDistrictId = Number(
		currentUser?.district_id || currentUser?.district,
	);
	const isAuditCalendarBackedScheme =
		selectedSchemeId === 1 || selectedSchemeId === 2;

	// The logged-in BRP's currently ongoing (Present) posting from their
	// Staff Registration Working Details — used to auto-fill Section A:
	// BRP Details' "Present District"/"Present Block" on a brand-new entry.
	// Left empty when they haven't marked any station as Present yet
	// (never falls back to their home/registered district).
	const { data: brpPresentLocationRaw } = useQuery({
		queryKey: ["brp-present-location", currentUser?.employee_code],
		queryFn: () => fetchDistrictEmployeeByCode(currentUser.employee_code),
		enabled: !id && !!currentUser?.employee_code,
	});
	const brpPresentLocation = Array.isArray(brpPresentLocationRaw)
		? brpPresentLocationRaw[0]
		: brpPresentLocationRaw;

	const { data: expenditureYears } = useExpenditureYears();
	const { data: auditYears } = useAuditYears();
	const [selectedDrpRoleId, setSelectedDrpRoleId] = useState<number | null>(
		Number(initialData?.drp_role_id) || 3,
	);
	const { data: employeeCodes } = useEmployeeCodesByRoleId(selectedDrpRoleId);

	const hlcDistrictId = selectedDistrict || currentUserDistrictId;
	const hlcSchemeId = selectedSchemeId || 1;
	const { data: hlcRegNumbers = [] } = useHlcRegistrationNumbers(
		hlcDistrictId,
		hlcSchemeId,
	);

	const [brpSelectedDistrict, setBrpSelectedDistrict] = useState<number | null>(
		initialData?.brp_district_id
			? Number(initialData.brp_district_id)
			: currentUser?.district_id
				? Number(currentUser.district_id)
				: currentUser?.district
					? Number(currentUser.district)
					: null,
	);

	useEffect(() => {
		const distId = Number(
			initialData?.brp_district_id ||
				currentUser?.district_id ||
				currentUser?.district,
		);
		if (distId) {
			setBrpSelectedDistrict(distId);
		}
	}, [initialData, currentUser]);

	const { data: brpBlocksData } = useBlocksByDistrict(brpSelectedDistrict || 0);
	const brpBlocks = brpBlocksData || [];

	const initialFormValues: MGNREGSFormData = {
		brp_employee_code:
			initialData?.brp_employee_code || currentUser?.employee_code || "",
		brp_name: initialData?.brp_name || currentUser?.name || "",
		brp_mobile_no: initialData?.brp_mobile_no || currentUser?.mobile || "",
		brp_district_id:
			initialData?.brp_district_id != null &&
			initialData?.brp_district_id !== ""
				? Number(initialData.brp_district_id)
				: currentUser?.district_id != null && currentUser?.district_id !== ""
					? Number(currentUser.district_id)
					: currentUser?.district != null && currentUser?.district !== ""
						? Number(currentUser.district)
						: null,
		brp_block_id:
			initialData?.brp_block_id != null && initialData?.brp_block_id !== ""
				? Number(initialData.brp_block_id)
				: currentUser?.block_id != null && currentUser?.block_id !== ""
					? Number(currentUser.block_id)
					: currentUser?.block != null && currentUser?.block !== ""
						? Number(currentUser.block)
						: null,
		district_id:
			initialData?.district_id != null && initialData?.district_id !== ""
				? Number(initialData.district_id)
				: null,
		block_id:
			initialData?.block_id != null && initialData?.block_id !== ""
				? Number(initialData.block_id)
				: null,
		village_id: initialData?.village_id || null,
		vlg_code: initialData?.vlg_code || "",
		round_no: initialData?.round_no || null,
		audit_start_date: initialData?.audit_start_date || "",
		audit_end_date: initialData?.audit_end_date || "",
		sgs_date: initialData?.sgs_date || "",
		sa_period_from_date: initialData?.sa_period_from_date || "",
		sa_period_to_date: initialData?.sa_period_to_date || "",
		expenditure_year_id: initialData?.expenditure_year_id || null,
		audit_year_id: initialData?.audit_year_id || null,
		observer:
			initialData?.observer === "true" ||
			initialData?.observer === true ||
			initialData?.observer === 1 ||
			initialData?.observer === "1",
		observer_name: initialData?.observer_name || "",
		observer_designation: initialData?.observer_designation || "",
		coram: initialData?.coram || null,
		cc_rav: initialData?.cc_rav || "",
		drp_role_id: initialData?.drp_role_id || 3,
		drp_employee_code: initialData?.drp_employee_code || "",
		drp_name: initialData?.drp_name || "",
		drp_mobile_no: initialData?.drp_mobile_no || "",
		drp_district_name: initialData?.drp_district_name || "",
		vrp_details:
			parseJsonField(initialData?.vrp_details).length > 0
				? parseJsonField(initialData?.vrp_details)
				: !id
					? [{ mobile_no: "", name: "", employee_code: "", village_name: "", village_id: null, days_worked: null }]
					: [],
		total_works: initialData?.total_works || null,
		unskilled_amount: initialData?.unskilled_amount || null,
		skilled_semi_skilled_amount:
			initialData?.skilled_semi_skilled_amount || null,
		material_expenditure_amount:
			initialData?.material_expenditure_amount || null,
		total_expenditure_amount: initialData?.total_expenditure_amount || null,
		number_of_works_verified: initialData?.number_of_works_verified || null,
		total_households_worked: initialData?.total_households_worked || null,
		total_households_verified: initialData?.total_households_verified || null,
		report_file: initialData?.report_file || null,
		case_study_1_type: initialData?.case_study_1_type || "",
		case_study_1_file: initialData?.case_study_1_file || null,
		case_study_2_type: initialData?.case_study_2_type || "",
		case_study_2_file: initialData?.case_study_2_file || null,
		photo_1: initialData?.photo_1 || null,
		photo_2: initialData?.photo_2 || null,
		photo_3: initialData?.photo_3 || null,
		issues: parseJsonField(initialData?.issues || initialData?.para_details),
	};

	const initialPMAYGValues: PMAYGFormData = {
		brp_employee_code:
			initialData?.brp_employee_code || currentUser?.employee_code || "",
		brp_name: initialData?.brp_name || currentUser?.name || "",
		brp_mobile_no: initialData?.brp_mobile_no || currentUser?.mobile || "",
		brp_district_id:
			initialData?.brp_district_id != null &&
			initialData?.brp_district_id !== ""
				? Number(initialData.brp_district_id)
				: currentUser?.district_id != null && currentUser?.district_id !== ""
					? Number(currentUser.district_id)
					: currentUser?.district != null && currentUser?.district !== ""
						? Number(currentUser.district)
						: null,
		brp_block_id:
			initialData?.brp_block_id != null && initialData?.brp_block_id !== ""
				? Number(initialData.brp_block_id)
				: currentUser?.block_id != null && currentUser?.block_id !== ""
					? Number(currentUser.block_id)
					: currentUser?.block != null && currentUser?.block !== ""
						? Number(currentUser.block)
						: null,
		district_id: initialData?.district_id || null,
		block_id: initialData?.block_id || null,
		village_id: initialData?.village_id || null,
		vlg_code: initialData?.vlg_code || "",
		round_no: initialData?.round_no || null,
		audit_start_date: initialData?.audit_start_date || "",
		audit_end_date: initialData?.audit_end_date || "",
		sgs_date: initialData?.sgs_date || "",
		sa_period_from_date: initialData?.sa_period_from_date || "",
		sa_period_to_date: initialData?.sa_period_to_date || "",
		expenditure_year_id: initialData?.expenditure_year_id || null,
		audit_year_id: initialData?.audit_year_id || null,
		observer:
			initialData?.observer === "true" ||
			initialData?.observer === true ||
			initialData?.observer === 1 ||
			initialData?.observer === "1",
		observer_name: initialData?.observer_name || "",
		observer_designation: initialData?.observer_designation || "",
		coram: initialData?.coram || null,
		cc_rav: initialData?.cc_rav || "",
		total_houses: initialData?.total_houses || null,
		no_of_1st_installments: initialData?.no_of_1st_installments || null,
		no_of_2nd_installments: initialData?.no_of_2nd_installments || null,
		no_of_3rd_installments: initialData?.no_of_3rd_installments || null,
		no_of_4th_installments: initialData?.no_of_4th_installments || null,
		houses_not_completed_after_4th:
			initialData?.houses_not_completed_after_4th || null,
		gs_decision:
			initialData?.gs_decision === "true" ||
			initialData?.gs_decision === true ||
			initialData?.gs_decision === 1 ||
			initialData?.gs_decision === "1",
		project_deficiencies: initialData?.project_deficiencies || "",
		special_remarks: initialData?.special_remarks || "",
		outcome_of_audit: initialData?.outcome_of_audit || "",
		secc_list_count: initialData?.secc_list_count || 0,
		secc_non_rejected: initialData?.secc_non_rejected || 0,
		secc_selected: initialData?.secc_selected || 0,
		awaas_plus_list_count: initialData?.awaas_plus_list_count || 0,
		awaas_plus_selected: initialData?.awaas_plus_selected || 0,
		total_selected_mis: initialData?.total_selected_mis || 0,
		no_of_beneficiaries_interviewed:
			initialData?.no_of_beneficiaries_interviewed || 0,
		not_interviewed_but_house_visited:
			initialData?.not_interviewed_but_house_visited || 0,
		could_not_identify: initialData?.could_not_identify || 0,
		total_verified_field: initialData?.total_verified_field || 0,
		secc_beneficiaries_in_kutcha_houses:
			initialData?.secc_beneficiaries_in_kutcha_houses || 0,
		report_file: initialData?.report_file || null,
		issues: parseJsonField(initialData?.issues || initialData?.para_details),
	};

	const handleSubmit = async (values: MGNREGSFormData | PMAYGFormData) => {
		try {
			const submissionData: any = {
				...values,
				scheme_id: selectedSchemeId || (id ? 1 : null), // Fallback for edit mode if needed
				sub_scheme_id: selectedSubSchemeId || null,
			};

			// Add calculated values if they are MGNREGS
			if (
				"unskilled_amount" in values ||
				selectedSchemeId === 1 ||
				schemes
					?.find((s: any) => Number(s.id) === Number(selectedSchemeId))
					?.name?.includes("VB G RAM G")
			) {
				submissionData.total_expenditure_amount =
					calculateTotalExpenditure(values);
			}

			// Add calculated values if they are PMAYG
			if ("secc_selected" in values) {
				submissionData.total_selected_mis = calculatePMAYGMISTotal(values);
				submissionData.total_verified_field = calculatePMAYGFieldTotal(values);
			}

			// MGNREGS / VB G RAM G only: block a second audit entry for the same
			// District + Block + Panchayat + Gram Sabha Date (any user, not just
			// the current one) — a new submission, not an edit of an existing one.
			if (!isEdit && "unskilled_amount" in values) {
				const mgnregsValues = values as MGNREGSFormData;
				const { items: existingEntries } = await fetchSchemeEntriesForReport({
					scheme_id: String(submissionData.scheme_id),
					district_id: String(mgnregsValues.district_id || ""),
					limit: 500,
				});
				const duplicate = (existingEntries || []).find(
					(row: any) =>
						Number(row.block_id) === Number(mgnregsValues.block_id) &&
						Number(row.village_id) === Number(mgnregsValues.village_id) &&
						row.sgs_date === mgnregsValues.sgs_date,
				);
				if (duplicate) {
					toast.error(
						"Already this scheme applied for the selected District, Block, Panchayat and Gram Sabha Date.",
					);
					return;
				}
			}

			if (isEdit && id) {
				await updateMutation.mutateAsync({ id, data: submissionData });
				toast.success("Report updated successfully");
			} else {
				await submitMutation.mutateAsync(submissionData);
				toast.success("Report submitted successfully");
			}
			navigate("/scheme-data-entry");
		} catch (error: any) {
			console.error("Submission error:", error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				"Failed to submit report";
			toast.error(errorMessage);
		}
	};

	const calculateTotalExpenditure = (values: any) => {
		const unskilled = Number(values.unskilled_amount) || 0;
		const skilled = Number(values.skilled_semi_skilled_amount) || 0;
		const material = Number(values.material_expenditure_amount) || 0;
		return unskilled + skilled + material;
	};

	const calculatePMAYGMISTotal = (values: any) => {
		return (
			(Number(values.secc_selected) || 0) +
			(Number(values.awaas_plus_selected) || 0)
		);
	};

	const calculatePMAYGFieldTotal = (values: any) => {
		return (
			(Number(values.no_of_beneficiaries_interviewed) || 0) +
			(Number(values.not_interviewed_but_house_visited) || 0) +
			(Number(values.could_not_identify) || 0)
		);
	};

	const isNmpSelected = schemes
		?.find((s: any) => Number(s.id) === Number(selectedSchemeId))
		?.name?.includes("NMP");
	const isCmbfsSelected = schemes
		?.find((s: any) => Number(s.id) === Number(selectedSchemeId))
		?.name?.includes("CMBFS");
	const isVbGramGSelected = schemes
		?.find((s: any) => Number(s.id) === Number(selectedSchemeId))
		?.name?.includes("VB G RAM G");
	const isBlockDistrictAssemblySelected = Number(selectedSchemeId) === 8;
	const isHlcSelected = Number(selectedSchemeId) === 7;
	const needsSubSchemeSelection =
		!id && !!selectedSchemeId && subSchemes.length > 0 && !selectedSubSchemeId;

	if (isInitialLoading) {
		return <div className="text-center p-10">Loading audit data...</div>;
	}

	return (
		<>
			<PageTitle>
				{isView ? "View Audit" : isEdit ? "Edit Audit" : "Scheme Data Entry"}
			</PageTitle>

			{/* SCHEME SELECTION SECTION - Only shown when adding */}
			{!id && (
				<div className="card shadow-sm border-0 mb-8 rounded-4 overflow-hidden">
					<div className="card-header border-0 pt-6 pb-2 bg-light-primary bg-opacity-50">
						<h3 className="card-title align-items-start flex-column">
							<span className="card-label fw-bolder text-dark fs-2 mb-1">
								Select Scheme
							</span>
							<span className="text-muted mt-1 fw-bold fs-7">
								Choose a scheme to open the entry form
							</span>
						</h3>
						<div className="card-toolbar">
							<button
								type="button"
								onClick={() => navigate("/scheme-data-entry")}
								className="btn btn-sm btn-outline btn-outline-primary btn-active-primary bg-white fw-bolder shadow-sm px-4 py-2"
							>
								<i className="bi bi-arrow-left fs-4 me-2"></i>
								Back to List
							</button>
						</div>
					</div>
					<div className="card-body py-6">
						<div className="d-flex flex-wrap gap-4">
							{isSchemesLoading ? (
								<div className="d-flex align-items-center text-primary fw-bolder fs-5 p-4">
									<span
										className="spinner-border spinner-border-sm me-3"
										role="status"
										aria-hidden="true"
									></span>
									Loading schemes...
								</div>
							) : (
								schemes?.map((scheme) => {
									const isActive = selectedSchemeId === scheme.id;
									return (
										<button
											key={scheme.id}
											type="button"
											onClick={() => handleSelectScheme(scheme.id)}
											className={`btn d-flex align-items-center justify-content-center px-6 py-4 transition-300 ${
												isActive
													? "btn-primary shadow fs-4 fw-bolder rounded-pill"
													: "btn-outline btn-outline-dashed btn-outline-default bg-light text-gray-700 btn-active-light-primary fs-5 fw-bold rounded-4 hover-elevate-up"
											}`}
											style={{
												minWidth: "120px",
											}}
										>
											{isActive && (
												<i className="bi bi-check-circle-fill fs-3 me-2 text-white"></i>
											)}
											{scheme.name}
										</button>
									);
								})
							)}
						</div>
					</div>
				</div>
			)}

			{/* SUB-SCHEME SELECTION - shown when the selected scheme (e.g. DSJE) has sub-schemes */}
			{needsSubSchemeSelection && (
				<div className="card shadow-sm border-0 mb-8 rounded-4 overflow-hidden">
					<div className="card-header border-0 pt-6 pb-2 bg-light-warning bg-opacity-50">
						<h3 className="card-title align-items-start flex-column">
							<span className="card-label fw-bolder text-dark fs-2 mb-1">
								{schemes?.find((s: any) => s.id === selectedSchemeId)?.name}
							</span>
							{/* <span className="text-muted mt-1 fw-bold fs-7">
								Choose a sub-scheme to open its entry form
							</span> */}
						</h3>
						<div className="card-toolbar">
							<button
								type="button"
								onClick={() => setSelectedSchemeId(null)}
								className="btn btn-sm btn-outline btn-outline-primary btn-active-primary bg-white fw-bolder shadow-sm px-4 py-2"
							>
								<i className="bi bi-arrow-left fs-4 me-2"></i>
								Back to Schemes
							</button>
						</div>
					</div>
					<div className="card-body py-6">
						{isSubSchemesLoading ? (
							<div className="d-flex align-items-center text-primary fw-bolder fs-5 p-4">
								<span
									className="spinner-border spinner-border-sm me-3"
									role="status"
									aria-hidden="true"
								></span>
								Loading sub-schemes...
							</div>
						) : (
							<div className="row">
								<div className="col-md-6">
									<label className="form-label fw-bold text-gray-700 mb-2">
										Sub-scheme
									</label>
									<select
										className="form-select form-select-solid"
										value={selectedSubSchemeId ?? ""}
										onChange={(e) =>
											setSelectedSubSchemeId(
												e.target.value ? Number(e.target.value) : null,
											)
										}
									>
										<option value="">Select a sub-scheme</option>
										{[...subSchemes]
											.sort(
												(a, b) =>
													getDsjeSubSchemeOrder(a.name) -
													getDsjeSubSchemeOrder(b.name),
											)
											.map((subScheme) => (
												<option key={subScheme.id} value={subScheme.id}>
													{subScheme.name}
												</option>
											))}
									</select>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{(selectedSchemeId || id) && !needsSubSchemeSelection && (
				<>
					{/* MGNREGS FORM */}
					{(selectedSchemeId === 1 ||
						isVbGramGSelected ||
						(id && !selectedSchemeId)) && (
						<>
							<div className="card shadow-sm mb-5">
								<div className="card-header">
									<h3 className="card-title text-gray-800 fw-bold">
										{isView
											? "View MGNREGS / VB G RAM G Audit"
											: `${schemes?.find((s) => s.id === (selectedSchemeId || initialData?.scheme_id))?.name || (isVbGramGSelected ? "VB G RAM G" : "MGNREGS")} Data Entry`}
									</h3>
									<div className="card-toolbar">
										<button
											type="button"
											onClick={() => navigate("/scheme-data-entry")}
											className="btn btn-sm btn-light-primary"
										>
											<KTIcon iconName="arrow-left" className="fs-3" />
											Back to List
										</button>
									</div>
								</div>
							</div>
							<Formik
								enableReinitialize={true}
								initialValues={initialFormValues}
								validationSchema={MGNREGSFormSchema}
								onSubmit={handleSubmit}
							>
								{({ values, setFieldValue }) => {
									// Looked up from the user's own District/Block/Panchayat/
									// Gram Sabha Date selection (not just district — a district
									// can have several audit calendar entries, one per
									// Panchayat) to auto-fill the derived Section B fields
									// (Round No/Audit dates/SA Period/Audit Year) only.
									const { data: latestAuditCalendar } = useLatestAuditCalendar(
										!id && isAuditCalendarBackedScheme ? 1 : null,
										!id && isAuditCalendarBackedScheme
											? Number(values.district_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? Number(values.block_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? Number(values.village_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? values.sgs_date || null
											: null,
									);
									return (
									<Form className="form">
										<SyncFormValues
											setFieldValue={setFieldValue}
											values={values}
											currentUser={currentUser}
											initialData={initialData}
											districts={districts}
											brpBlocks={brpBlocks}
											blocks={blocks}
											setBrpSelectedDistrict={setBrpSelectedDistrict}
											setSelectedDistrict={setSelectedDistrict}
											setSelectedBlock={setSelectedBlock}
											auditCalendarDefaults={latestAuditCalendar}
											brpPresentLocation={brpPresentLocation}
											auditYears={auditYears}
										/>
										{/* SECTION A: BRP DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section A: BRP Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Employee Code
														</label>
														<Field
															name="brp_employee_code"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
														<ErrorMessage
															name="brp_employee_code"
															component="div"
															className="text-danger fs-7"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Name
														</label>
														<Field
															name="brp_name"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Mobile No
														</label>
														<Field
															name="brp_mobile_no"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Present District
														</label>
														<Field
															as="select"
															name="brp_district_id"
															key={`brp-dist-${districts?.length || 0}-${values.brp_district_id || ""}`}
															className="form-select form-select-solid"
															disabled={true}
															value={
																values.brp_district_id != null
																	? String(values.brp_district_id)
																	: ""
															}
															onChange={() => {}}
														>
															<option value="">-</option>
															{districts?.map((d: any) => (
																<option key={d.id} value={String(d.id)}>
																	{d.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Present Block
														</label>
														<Field
															as="select"
															name="brp_block_id"
															key={`brp-blk-${brpBlocks?.length || 0}-${values.brp_block_id || ""}`}
															className="form-select form-select-solid"
															disabled={true}
															value={
																values.brp_block_id != null
																	? String(values.brp_block_id)
																	: ""
															}
															onChange={() => {}}
														>
															<option value="">-</option>
															{brpBlocks?.map((b: any) => (
																<option key={b.id} value={String(b.id)}>
																	{b.name}
																</option>
															))}
														</Field>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* (Existing Rest of MGNREGS sections follow, keeping as is for ID 1) */}

										{/* SECTION B: BASIC DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section B: Basic Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															District
														</label>
														<Field
															as="select"
															name="district_id"
															key={`secb-dist-${districts?.length || 0}-${values.district_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.district_id
																	? String(values.district_id)
																	: ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("district_id", val);
																setSelectedDistrict(val);
																setFieldValue("block_id", "");
																setFieldValue("village_id", "");
																setFieldValue("vlg_code", "");
																setSelectedBlock(null);
															}}
														>
															<option value="">Select District</option>
															{districts?.map((d: any) => (
																<option key={d.id} value={String(d.id)}>
																	{d.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Block
														</label>
														<Field
															as="select"
															name="block_id"
															key={`secb-blk-${blocks?.length || 0}-${values.block_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.block_id ? String(values.block_id) : ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("block_id", val);
																setSelectedBlock(val);
																setFieldValue("village_id", "");
																setFieldValue("vlg_code", "");
															}}
														>
															<option value="">Select Block</option>
															{blocks?.map((b: any) => (
																<option key={b.id} value={String(b.id)}>
																	{b.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Panchayat
														</label>
														<Field
															as="select"
															name="village_id"
															key={`secb-vlg-${villages?.length || 0}-${values.village_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.village_id
																	? String(values.village_id)
																	: ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("village_id", val);
																const selectedVillage = villages.find(
																	(v: any) => Number(v.id) === val,
																);
																if (selectedVillage) {
																	setFieldValue(
																		"vlg_code",
																		selectedVillage.vlg_code ||
																			selectedVillage.code ||
																			"",
																	);
																} else {
																	setFieldValue("vlg_code", "");
																}
															}}
														>
															<option value="">Select Panchayat</option>
															{villages?.map((v: any) => (
																<option key={v.id} value={String(v.id)}>
																	{v.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															LGD Code
														</label>
														<Field
															name="vlg_code"
															className="form-control form-control-solid"
															disabled={isView || isEdit}
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Gram Sabha Date
														</label>
														<Field
															name="sgs_date"
															type="date"
															min={getMinGramSabhaDate(values.audit_end_date)}
															className="form-control form-control-solid"
															disabled={isView || isEdit}
														/>
														<ErrorMessage
															name="sgs_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Round No
														</label>
														<Field
															name="round_no"
															type="text"
															className="form-control form-control-solid"
															disabled={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit Start Date
														</label>
														<Field
															name="audit_start_date"
															type="date"
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="audit_start_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit End Date
														</label>
														<Field
															name="audit_end_date"
															type="date"
															min={values.audit_start_date}
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="audit_end_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SA Period From Date
														</label>
														<Field
															name="sa_period_from_date"
															type="date"
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="sa_period_from_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SA Period To Date
														</label>
														<Field
															name="sa_period_to_date"
															type="date"
															min={values.sa_period_from_date}
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="sa_period_to_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit Year
														</label>
														<Field
															as="select"
															name="audit_year_id"
															className="form-select form-select-solid"
															disabled={true}
														>
															<option value="">Select Year</option>
															{auditYears?.map((year: any) => (
																<option key={year.id} value={year.id}>
																	{year.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Quoram
														</label>
														<Field
															name="coram"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
												</div>

												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Observer
														</label>
														<div className="d-flex align-items-center mt-3">
															<label className="form-check form-check-custom form-check-solid me-5">
																<input
																	type="radio"
																	name="observer"
																	checked={values.observer === true}
																	onChange={() =>
																		setFieldValue("observer", true)
																	}
																	className="form-check-input"
																	disabled={isView}
																/>
																<span className="form-check-label text-gray-700">
																	Yes
																</span>
															</label>
															<label className="form-check form-check-custom form-check-solid">
																<input
																	type="radio"
																	name="observer"
																	checked={
																		values.observer === false ||
																		!values.observer
																	}
																	onChange={() =>
																		setFieldValue("observer", false)
																	}
																	className="form-check-input"
																	disabled={isView}
																/>
																<span className="form-check-label text-gray-700">
																	No
																</span>
															</label>
														</div>
													</div>
												</div>

												{values.observer && (
													<div className="row g-9 mb-5 mt-2 p-5 border rounded bg-light-lighten">
														<div className="col-md-6 fv-row">
															<label className="form-label required fs-5 fw-bold text-dark">
																Observer Name
															</label>
															<Field
																name="observer_name"
																className="form-control form-control-solid"
																disabled={isView}
															/>
														</div>
														<div className="col-md-6 fv-row">
															<label className="form-label required fs-5 fw-bold text-dark">
																Observer Designation
															</label>
															<Field
																name="observer_designation"
																className="form-control form-control-solid"
																disabled={isView}
															/>
														</div>
													</div>
												)}
											</KTCardBody>
										</KTCard>

										{/* SECTION C: DRP / DRP I/C & VRP DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section C: DRP / DRP I/C & VRP DETAILS
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<h5 className="mb-4">DRP/DRP I/C Details</h5>
												<div className="row g-5 mb-8">
													<div className="col-md-2 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Role
														</label>
														<Field
															as="select"
															name="drp_role_id"
															className="form-select form-select-solid"
															disabled={isView}
															onChange={(e: any) => {
																const val = Number(e.target.value);
																setFieldValue("drp_role_id", val);
																setSelectedDrpRoleId(val);
																setFieldValue("drp_employee_code", "");
																setFieldValue("drp_name", "");
																setFieldValue("drp_mobile_no", "");
																setFieldValue("drp_district_name", "");
															}}
														>
															<option value={3}>DRP</option>
															<option value={6}>DRP I/C</option>
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Employee Code
														</label>
														<Field
															as="select"
															name="drp_employee_code"
															className="form-select form-select-solid"
															disabled={isView}
															onChange={async (e: any) => {
																const val = e.target.value;
																setFieldValue("drp_employee_code", val);
																if (val) {
																	try {
																		const details =
																			await fetchDistrictEmployeeByCode(val);
																		const employee = Array.isArray(details)
																			? details[0]
																			: details;
																		if (employee) {
																			setFieldValue(
																				"drp_name",
																				employee.name || "",
																			);
																			setFieldValue(
																				"drp_mobile_no",
																				employee.mobile_no ||
																					employee.mobile ||
																					"",
																			);
																			setFieldValue(
																				"drp_district_name",
																				employee.present_district_name || "",
																			);
																		}
																	} catch (error) {
																		console.error(
																			"Failed to fetch DRP details",
																			error,
																		);
																	}
																} else {
																	setFieldValue("drp_name", "");
																	setFieldValue("drp_mobile_no", "");
																	setFieldValue("drp_district_name", "");
																}
															}}
														>
															<option value="">Select Employee</option>
															{employeeCodes?.map((emp: any) => (
																<option
																	key={emp.id || emp.employee_code}
																	value={emp.employee_code}
																>
																	{emp.employee_code}{emp.name ? ` - ${emp.name}` : ""}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-2 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Name
														</label>
														<Field
															name="drp_name"
															className="form-control form-control-solid"
															disabled
														/>
													</div>
													<div className="col-md-2 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Contact
														</label>
														<Field
															name="drp_mobile_no"
															className="form-control form-control-solid"
															disabled
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Present District
														</label>
														<Field
															name="drp_district_name"
															className="form-control form-control-solid"
															disabled
														/>
													</div>
												</div>

												<div className="separator separator-dashed my-5"></div>
												<div className="d-flex justify-content-between align-items-center mb-4">
													<h5 className="mb-0">VRP Details</h5>
													{!isView && values.vrp_details.length < 5 && (
														<button
															type="button"
															className="btn btn-sm btn-dark d-flex align-items-center gap-1"
															onClick={() => {
																if (values.vrp_details.length >= 5) {
																	toast.error("Maximum 5 VRPs allowed");
																	return;
																}
																const newVrp = {
																	mobile_no: "",
																	name: "",
																	employee_code: "",
																	village_name: "",
																	village_id: null,
																	days_worked: null,
																};
																setFieldValue("vrp_details", [
																	...values.vrp_details,
																	newVrp,
																]);
															}}
														>
															<KTIcon iconName="plus" className="fs-3" /> Add VRP
														</button>
													)}
												</div>

												{values.vrp_details.map((vrp: any, index: number) => (
													<div
														key={index}
														className="border p-4 rounded mb-3 bg-white shadow-sm"
													>
														<div className="row g-5 align-items-end">
															<div className="col-md-2 fv-row">
																<label className="form-label fs-7 fw-bold text-dark">
																	Search VRP by Contact No.
																</label>
																<Field
																	name={`vrp_details.${index}.mobile_no`}
																	className="form-control form-control-solid"
																	placeholder="Enter 10-digit number"
																	disabled={isView}
																	onChange={async (e: any) => {
																		const val = e.target.value;
																		setFieldValue(
																			`vrp_details.${index}.mobile_no`,
																			val,
																		);
																		if (val && val.length === 10) {
																			try {
																				const details =
																					await fetchVillageEmployeeByMobile(val);
																				const vrpData = Array.isArray(details)
																					? details[0]
																					: details;
																				if (vrpData) {
																					setFieldValue(
																						`vrp_details.${index}.name`,
																						vrpData.name || "",
																					);
																					setFieldValue(
																						`vrp_details.${index}.employee_code`,
																						vrpData.employee_code || "",
																					);
																					setFieldValue(
																						`vrp_details.${index}.village_name`,
																						vrpData.village_name ||
																							vrpData.village?.name ||
																							"",
																					);
																					setFieldValue(
																						`vrp_details.${index}.village_id`,
																						vrpData.village_id || null,
																					);
																				} else {
																					toast.info("No VRP found for this contact number");
																				}
																			} catch (error) {
																				console.error(
																					"Failed to fetch VRP details",
																					error,
																				);
																			}
																		}
																	}}
																/>
															</div>
															<div className="col-md-2 fv-row">
																<label className="form-label fs-7 fw-bold text-dark">
																	VRP Name
																</label>
																<Field
																	name={`vrp_details.${index}.name`}
																	className="form-control form-control-solid"
																	placeholder="VRP Name"
																	disabled={isView}
																/>
															</div>
															<div className="col-md-2 fv-row">
																<label className="form-label fs-7 fw-bold text-dark">
																	VRP Employee Code
																</label>
																<Field
																	name={`vrp_details.${index}.employee_code`}
																	className="form-control form-control-solid"
																	placeholder="VRP Employee Code"
																	disabled={isView}
																/>
															</div>
															<div className="col-md-2 fv-row">
																<label className="form-label fs-7 fw-bold text-dark">
																	VRP Panchayat
																</label>
																<Field
																	name={`vrp_details.${index}.village_name`}
																	className="form-control form-control-solid"
																	placeholder="VRP Panchayat"
																	disabled={isView}
																/>
															</div>
															<div className="col-md-2 fv-row">
																<label className="form-label fs-7 fw-bold text-dark">
																	Number of Days Worked
																</label>
																<Field
																	name={`vrp_details.${index}.days_worked`}
																	type="number"
																	className="form-control form-control-solid"
																	placeholder="Days Worked"
																	disabled={isView}
																	onKeyDown={blockInvalidNumberKeys}
																/>
															</div>
															{!isView && index > 0 && (
																<div className="col-md-1 fv-row text-end">
																	<button
																		type="button"
																		className="btn btn-icon btn-sm btn-light-danger"
																		onClick={() => {
																			const newList = values.vrp_details.filter(
																				(_: any, i: number) => i !== index,
																			);
																			setFieldValue("vrp_details", newList);
																		}}
																	>
																		<KTIcon iconName="trash" className="fs-3" />
																	</button>
																</div>
															)}
														</div>
													</div>
												))}
											</KTCardBody>
										</KTCard>

										{/* SECTION D: VERIFICATION DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section D: Verification Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Works
														</label>
														<Field
															name="total_works"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Unskilled - Amount
														</label>
														<Field
															name="unskilled_amount"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Skilled/Semi Skilled - Amount
														</label>
														<Field
															name="skilled_semi_skilled_amount"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Material Expenditure Amount
														</label>
														<Field
															name="material_expenditure_amount"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label fw-bold">
															Total Expenditure Amount
														</label>
														<input
															name="total_expenditure_amount"
															type="number"
															className="form-control form-control-solid bg-light-warning"
															value={calculateTotalExpenditure(values)}
															readOnly
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Number of Works Verified
														</label>
														<Field
															name="number_of_works_verified"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Households Worked
														</label>
														<Field
															name="total_households_worked"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Households Verified
														</label>
														<Field
															name="total_households_verified"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION E: REPORT UPLOAD */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section E: Report Upload
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="mb-10">
													<FileUpload
														label="Audit Report (PDF format)"
														value={values.report_file}
														disabled={isView}
														onChange={(val) =>
															setFieldValue("report_file", val)
														}
														onUpload={async (file) => {
															if (
																file &&
																file.type !== "application/pdf" &&
																!file.name.toLowerCase().endsWith(".pdf")
															) {
																toast.error(
																	"Only PDF format files are allowed!",
																);
																throw new Error(
																	"Only PDF format files are allowed!",
																);
															}
															if (file && file.size > 2 * 1024 * 1024) {
																toast.error("File size exceeds 2MB limit!");
																throw new Error("File size exceeds 2MB limit!");
															}
															return await uploadMutation.mutateAsync(file);
														}}
														accept="application/pdf,.pdf"
														maxSizeMB={2}
														description="Max 2MB (PDF format only)"
													/>
												</div>
											</KTCardBody>
										</KTCard>

										{/* Case Studies */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Case Studies
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-6 align-items-center">
													<div className="col-md-6 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Case Study 1 Type
														</label>
														<Field
															as="select"
															name="case_study_1_type"
															className="form-select form-select-solid"
															disabled={isView}
															onChange={(e: any) => {
																const val = e.target.value;
																setFieldValue("case_study_1_type", val);
																if (!val) {
																	setFieldValue("case_study_1_file", null);
																}
															}}
														>
															<option value="">Select Case Study 1 Type</option>
															{caseStudyOptions.map((opt) => (
																<option key={opt} value={opt}>
																	{opt}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-6 fv-row">
														{values.case_study_1_type && (
															<FileUpload
																label="Case Study 1 File (PDF / DOCX)"
																value={values.case_study_1_file}
																disabled={isView}
																onChange={(val) =>
																	setFieldValue("case_study_1_file", val)
																}
																onUpload={async (file) => {
																	const ext = file.name.toLowerCase();
																	const isValidType =
																		file.type === "application/pdf" ||
																		file.type ===
																			"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
																		file.type === "application/msword" ||
																		ext.endsWith(".pdf") ||
																		ext.endsWith(".docx") ||
																		ext.endsWith(".doc");
																	if (!isValidType) {
																		toast.error(
																			"Only PDF and DOCX format files are allowed!",
																		);
																		throw new Error(
																			"Only PDF and DOCX format files are allowed!",
																		);
																	}
																	return await uploadSchemeReport(
																		file,
																		"mgnregs-case-study",
																	);
																}}
																accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
																maxSizeMB={5}
																description="Max 5MB (PDF or DOCX format)"
															/>
														)}
													</div>
												</div>
												<div className="row g-9 align-items-center">
													<div className="col-md-6 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Case Study 2 Type
														</label>
														<Field
															as="select"
															name="case_study_2_type"
															className="form-select form-select-solid"
															disabled={isView}
															onChange={(e: any) => {
																const val = e.target.value;
																setFieldValue("case_study_2_type", val);
																if (!val) {
																	setFieldValue("case_study_2_file", null);
																}
															}}
														>
															<option value="">Select Case Study 2 Type</option>
															{caseStudyOptions.map((opt) => (
																<option key={opt} value={opt}>
																	{opt}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-6 fv-row">
														{values.case_study_2_type && (
															<FileUpload
																label="Case Study 2 File (PDF / DOCX)"
																value={values.case_study_2_file}
																disabled={isView}
																onChange={(val) =>
																	setFieldValue("case_study_2_file", val)
																}
																onUpload={async (file) => {
																	const ext = file.name.toLowerCase();
																	const isValidType =
																		file.type === "application/pdf" ||
																		file.type ===
																			"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
																		file.type === "application/msword" ||
																		ext.endsWith(".pdf") ||
																		ext.endsWith(".docx") ||
																		ext.endsWith(".doc");
																	if (!isValidType) {
																		toast.error(
																			"Only PDF and DOCX format files are allowed!",
																		);
																		throw new Error(
																			"Only PDF and DOCX format files are allowed!",
																		);
																	}
																	return await uploadSchemeReport(
																		file,
																		"mgnregs-case-study",
																	);
																}}
																accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
																maxSizeMB={5}
																description="Max 5MB (PDF or DOCX format)"
															/>
														)}
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* Audit Photos */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Audit Photos
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9">
													<div className="col-md-4 fv-row">
														<FileUpload
															label="Audit Photo 1"
															value={values.photo_1}
															disabled={isView}
															required={true}
															onChange={(val) => setFieldValue("photo_1", val)}
															onUpload={(file) =>
																uploadSchemeReport(file, "mgnregs-audit-photo")
															}
															accept="image/*"
															maxSizeMB={2}
															description="Compulsory (Max 2MB, JPG/PNG)"
														/>
														<div className="text-danger mt-1 fw-bold fs-7">
															<ErrorMessage name="photo_1" />
														</div>
													</div>
													<div className="col-md-4 fv-row">
														<FileUpload
															label="Audit Photo 2"
															value={values.photo_2}
															disabled={isView}
															onChange={(val) => setFieldValue("photo_2", val)}
															onUpload={(file) =>
																uploadSchemeReport(file, "mgnregs-audit-photo")
															}
															accept="image/*"
															maxSizeMB={2}
															description="Optional (Max 2MB, JPG/PNG)"
														/>
													</div>
													<div className="col-md-4 fv-row">
														<FileUpload
															label="Audit Photo 3"
															value={values.photo_3}
															disabled={isView}
															onChange={(val) => setFieldValue("photo_3", val)}
															onUpload={(file) =>
																uploadSchemeReport(file, "mgnregs-audit-photo")
															}
															accept="image/*"
															maxSizeMB={2}
															description="Optional (Max 2MB, JPG/PNG)"
														/>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION F: PARA PARTICULARS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section F: Para Particulars
													</span>
												</h3>
												<div className="card-toolbar">
													{!isView && (
														<button
															type="button"
															className="btn btn-sm btn-dark"
															onClick={() => {
																const newIssue: ParaIssue = {
																	issue_no: "",
																	issue_type_id: null,
																	issue_category_id: null,
																	issue_sub_category_id: null,
																	code_no: "",
																	description: "",
																	no_of_cases: null,
																	amount: null,
																	recovered_amount: null,
																	hlc_reg_no: "",
																	status: "Pending",
																	hlc_recovery_amount: null,
																	nregs_issue_no: "",
																	grievance_total_individuals: null,
																	grievance_redressed_individuals: null,
																	status_reason: "",
																};
																setFieldValue("issues", [
																	...values.issues,
																	newIssue,
																]);
															}}
														>
															<KTIcon iconName="plus" className="fs-3" /> Add
															Issue
														</button>
													)}
												</div>
											</div>
											<KTCardBody className="py-3">
												{values.issues.map((issue: any, index: number) => {
													return (
														<ParaIssueRow
															key={index}
															index={index}
															isView={isView}
															isEdit={isEdit}
															isAdmin={userRoleId === 1}
															issue={issue}
															setFieldValue={setFieldValue}
															schemeId={selectedSchemeId || 1}
															hlcOptions={hlcRegNumbers}
															remove={() => {
																const newList = values.issues.filter(
																	(_: any, i: number) => i !== index,
																);
																setFieldValue("issues", newList);
															}}
														/>
													);
												})}
											</KTCardBody>
										</KTCard>

										{/* SUBMIT BUTTON */}
										<div className="d-flex justify-content-end mb-10">
											<button
												type="button"
												className="btn btn-light me-3"
												onClick={() => navigate("/scheme-data-entry")}
											>
												Cancel
											</button>
											{!isView && (
												<button
													type="submit"
													className="btn btn-primary"
													disabled={
														submitMutation.isPending || updateMutation.isPending
													}
												>
													{submitMutation.isPending || updateMutation.isPending
														? "Submitting..."
														: isEdit
															? "Update MGNREGS / VB G RAM G"
															: "Submit MGNREGS / VB G RAM G"}
												</button>
											)}
										</div>
									</Form>
								);
								}}
							</Formik>
						</>
					)}

					{/* PMAY-G FORM - ID: 2 */}
					{selectedSchemeId === 2 && (
						<>
							<div className="card shadow-sm mb-5">
								<div className="card-header">
									<h3 className="card-title text-gray-800 fw-bold">
										PMAY-G Data Entry
									</h3>
									<div className="card-toolbar">
										<button
											type="button"
											onClick={() => navigate("/scheme-data-entry")}
											className="btn btn-sm btn-light-primary"
										>
											<KTIcon iconName="arrow-left" className="fs-3" />
											Back to List
										</button>
									</div>
								</div>
							</div>

							<Formik
								enableReinitialize={true}
								initialValues={initialPMAYGValues}
								onSubmit={handleSubmit}
							>
								{({ values, setFieldValue }) => {
									// Looked up from the user's own District/Block/Panchayat/
									// Gram Sabha Date selection (not just district — a district
									// can have several audit calendar entries, one per
									// Panchayat) to auto-fill the derived Section B fields
									// (Round No/Audit dates/SA Period/Audit Year) only.
									const { data: latestAuditCalendar } = useLatestAuditCalendar(
										!id && isAuditCalendarBackedScheme ? 2 : null,
										!id && isAuditCalendarBackedScheme
											? Number(values.district_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? Number(values.block_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? Number(values.village_id) || null
											: null,
										!id && isAuditCalendarBackedScheme
											? values.sgs_date || null
											: null,
									);
									return (
									<Form className="form">
										<SyncFormValues
											setFieldValue={setFieldValue}
											values={values}
											currentUser={currentUser}
											initialData={initialData}
											districts={districts}
											brpBlocks={brpBlocks}
											blocks={blocks}
											setBrpSelectedDistrict={setBrpSelectedDistrict}
											setSelectedDistrict={setSelectedDistrict}
											setSelectedBlock={setSelectedBlock}
											brpPresentLocation={brpPresentLocation}
											auditCalendarDefaults={latestAuditCalendar}
											auditYears={auditYears}
										/>
										{/* SECTION A: BRP DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section A: BRP Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Employee Code
														</label>
														<Field
															name="brp_employee_code"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Name
														</label>
														<Field
															name="brp_name"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
													</div>
													<div className="col-md-2 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Mobile No
														</label>
														<Field
															name="brp_mobile_no"
															className="form-control form-control-solid"
															disabled={true}
															readOnly={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Present District
														</label>
														<Field
															as="select"
															name="brp_district_id"
															key={`gen-brp-dist-${districts?.length || 0}-${values.brp_district_id || ""}`}
															className="form-select form-select-solid"
															disabled={true}
															value={
																values.brp_district_id != null
																	? String(values.brp_district_id)
																	: ""
															}
															onChange={() => {}}
														>
															<option value="">-</option>
															{districts?.map((d: any) => (
																<option key={d.id} value={String(d.id)}>
																	{d.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Present Block
														</label>
														<Field
															as="select"
															name="brp_block_id"
															key={`gen-brp-blk-${brpBlocks?.length || 0}-${values.brp_block_id || ""}`}
															className="form-select form-select-solid"
															disabled={true}
															value={
																values.brp_block_id != null
																	? String(values.brp_block_id)
																	: ""
															}
															onChange={() => {}}
														>
															<option value="">-</option>
															{brpBlocks?.map((b: any) => (
																<option key={b.id} value={String(b.id)}>
																	{b.name}
																</option>
															))}
														</Field>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION B: BASIC DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section B: Basic Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															District
														</label>
														<Field
															as="select"
															name="district_id"
															key={`gen-secb-dist-${districts?.length || 0}-${values.district_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.district_id
																	? String(values.district_id)
																	: ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("district_id", val);
																setSelectedDistrict(val);
																setFieldValue("block_id", "");
																setFieldValue("village_id", "");
																setFieldValue("vlg_code", "");
																setSelectedBlock(null);
															}}
														>
															<option value="">Select District</option>
															{districts?.map((d: any) => (
																<option key={d.id} value={String(d.id)}>
																	{d.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Block
														</label>
														<Field
															as="select"
															name="block_id"
															key={`gen-secb-blk-${blocks?.length || 0}-${values.block_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.block_id ? String(values.block_id) : ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("block_id", val);
																setSelectedBlock(val);
																setFieldValue("village_id", "");
																setFieldValue("vlg_code", "");
															}}
														>
															<option value="">Select Block</option>
															{blocks?.map((b: any) => (
																<option key={b.id} value={String(b.id)}>
																	{b.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Panchayat
														</label>
														<Field
															as="select"
															name="village_id"
															key={`gen-secb-vlg-${villages?.length || 0}-${values.village_id || ""}`}
															className="form-select form-select-solid"
															disabled={isView || isEdit}
															value={
																values.village_id
																	? String(values.village_id)
																	: ""
															}
															onChange={(e: any) => {
																const val =
																	e.target.value !== ""
																		? Number(e.target.value)
																		: null;
																setFieldValue("village_id", val);
																const selectedVillage = villages.find(
																	(v: any) => Number(v.id) === val,
																);
																if (selectedVillage) {
																	setFieldValue(
																		"vlg_code",
																		selectedVillage.vlg_code ||
																			selectedVillage.code ||
																			"",
																	);
																} else {
																	setFieldValue("vlg_code", "");
																}
															}}
														>
															<option value="">Select Panchayat</option>
															{villages?.map((v: any) => (
																<option key={v.id} value={String(v.id)}>
																	{v.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															LGD Code
														</label>
														<Field
															name="vlg_code"
															className="form-control form-control-solid"
															disabled={isView || isEdit}
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Gram Sabha Date
														</label>
														<Field
															name="sgs_date"
															type="date"
															min={getMinGramSabhaDate(values.audit_end_date)}
															className="form-control form-control-solid"
															disabled={isView || isEdit}
														/>
														<ErrorMessage
															name="sgs_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Round No
														</label>
														<Field
															name="round_no"
															type="text"
															className="form-control form-control-solid"
															disabled={true}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit Start Date
														</label>
														<Field
															name="audit_start_date"
															type="date"
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="audit_start_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit End Date
														</label>
														<Field
															name="audit_end_date"
															type="date"
															min={values.audit_start_date}
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="audit_end_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SA Period From Date
														</label>
														<Field
															name="sa_period_from_date"
															type="date"
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="sa_period_from_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SA Period To Date
														</label>
														<Field
															name="sa_period_to_date"
															type="date"
															min={values.sa_period_from_date}
															className="form-control form-control-solid"
															disabled={true}
														/>
														<ErrorMessage
															name="sa_period_to_date"
															component="div"
															className="text-danger fs-7 mt-1"
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Audit Year
														</label>
														<Field
															as="select"
															name="audit_year_id"
															className="form-select form-select-solid"
															disabled={true}
														>
															<option value="">Select Year</option>
															{auditYears?.map((year: any) => (
																<option key={year.id} value={year.id}>
																	{year.name}
																</option>
															))}
														</Field>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Quoram
														</label>
														<Field
															name="coram"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
												</div>

												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Observer
														</label>
														<div className="d-flex align-items-center mt-3">
															<label className="form-check form-check-custom form-check-solid me-5">
																<input
																	type="radio"
																	name="observer"
																	checked={values.observer === true}
																	onChange={() =>
																		setFieldValue("observer", true)
																	}
																	className="form-check-input"
																	disabled={isView}
																/>
																<span className="form-check-label text-gray-700">
																	Yes
																</span>
															</label>
															<label className="form-check form-check-custom form-check-solid">
																<input
																	type="radio"
																	name="observer"
																	checked={
																		values.observer === false ||
																		!values.observer
																	}
																	onChange={() =>
																		setFieldValue("observer", false)
																	}
																	className="form-check-input"
																	disabled={isView}
																/>
																<span className="form-check-label text-gray-700">
																	No
																</span>
															</label>
														</div>
													</div>
												</div>

												{values.observer && (
													<div className="row g-9 mb-5 mt-2 p-5 border rounded bg-light-lighten">
														<div className="col-md-6 fv-row">
															<label className="form-label required fs-5 fw-bold text-dark">
																Observer Name
															</label>
															<Field
																name="observer_name"
																className="form-control form-control-solid"
																disabled={isView}
															/>
														</div>
														<div className="col-md-6 fv-row">
															<label className="form-label required fs-5 fw-bold text-dark">
																Observer Designation
															</label>
															<Field
																name="observer_designation"
																className="form-control form-control-solid"
																disabled={isView}
															/>
														</div>
													</div>
												)}
											</KTCardBody>
										</KTCard>

										{/* SECTION C: VERIFICATION DETAILS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section C: Verification Details
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="row g-9 mb-5">
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Houses
														</label>
														<Field
															name="total_houses"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															No. of 1st Installments
														</label>
														<Field
															name="no_of_1st_installments"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															No. of 2nd Installments
														</label>
														<Field
															name="no_of_2nd_installments"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
															onKeyDown={blockInvalidNumberKeys}
														/>
													</div>
												</div>
												<div className="row g-9 mb-5">
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															No. of 3rd Installments
														</label>
														<Field
															name="no_of_3rd_installments"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															No. of 4th Installments
														</label>
														<Field
															name="no_of_4th_installments"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Houses Not Completed After 4th installment
														</label>
														<Field
															name="houses_not_completed_after_4th"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION D: PANCHAYAT SUMMARY */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section D: Panchayat Summary
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="mb-5">
													<label className="form-label required fs-5 fw-bold text-dark">
														NEW Beneficiary GS Decision
													</label>
													<div className="d-flex align-items-center mt-3">
														<label className="form-check form-check-custom form-check-solid me-5">
															<Field
																name="gs_decision"
																type="radio"
																value={true}
																className="form-check-input"
																disabled={isView}
															/>
															<span className="form-check-label">Yes</span>
														</label>
														<label className="form-check form-check-custom form-check-solid">
															<Field
																name="gs_decision"
																type="radio"
																value={false}
																className="form-check-input"
																disabled={isView}
															/>
															<span className="form-check-label">No</span>
														</label>
													</div>
												</div>
												<div className="mb-5">
													<label className="form-label required fs-5 fw-bold text-dark">
														Project Deficiencies
													</label>
													<Field
														as="textarea"
														name="project_deficiencies"
														className="form-control form-control-solid"
														rows={3}
														disabled={isView}
													/>
												</div>
												<div className="mb-5">
													<label className="form-label required fs-5 fw-bold text-dark">
														Special Remarks
													</label>
													<Field
														as="textarea"
														name="special_remarks"
														className="form-control form-control-solid"
														rows={3}
														disabled={isView}
													/>
												</div>
												<div className="mb-5">
													<label className="form-label required fs-5 fw-bold text-dark">
														Outcome of Audit
													</label>
													<Field
														as="textarea"
														name="outcome_of_audit"
														className="form-control form-control-solid"
														rows={3}
														disabled={isView}
													/>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION E: PANCHAYAT VERIFICATION ANALYSIS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section E: Panchayat Verification Analysis
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<h6 className="fw-bold mb-4">A. As per MIS Report:</h6>
												<div className="row g-9 mb-5">
													<div className="col-md-4 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SECC List Count
														</label>
														<Field
															name="secc_list_count"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SECC Non-Rejected
														</label>
														<Field
															name="secc_non_rejected"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															SECC Selected
														</label>
														<Field
															name="secc_selected"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
												</div>
												<div className="row g-9 mb-8">
													<div className="col-md-4 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Awaas+ List Count
														</label>
														<Field
															name="awaas_plus_list_count"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Awaas+ Selected
														</label>
														<Field
															name="awaas_plus_selected"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-4 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Selected (MIS)
														</label>
														<input
															className="form-control form-control-solid bg-light"
															readOnly
															disabled
															value={calculatePMAYGMISTotal(values)}
														/>
													</div>
												</div>

												<h6 className="fw-bold mb-4">
													B. Field Verification Data:
												</h6>
												<div className="row g-9 mb-5">
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															No. of Beneficiaries Interviewed
														</label>
														<Field
															name="no_of_beneficiaries_interviewed"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Not Interviewed but House Visited
														</label>
														<Field
															name="not_interviewed_but_house_visited"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Could Not Identify
														</label>
														<Field
															name="could_not_identify"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
													<div className="col-md-3 fv-row">
														<label className="form-label fs-5 fw-bold text-dark">
															Total Verified (Field)
														</label>
														<input
															className="form-control form-control-solid bg-light"
															readOnly
															disabled
															value={calculatePMAYGFieldTotal(values)}
														/>
													</div>
												</div>

												<h6 className="fw-bold mb-4">
													C. Format 3 – SECC Beneficiaries in Kutcha Houses:
												</h6>
												<div className="row g-9 mb-5">
													<div className="col-md-6 fv-row">
														<label className="form-label required fs-5 fw-bold text-dark">
															Count of SECC-selected beneficiaries still in
															Kutcha Houses
														</label>
														<Field
															name="secc_beneficiaries_in_kutcha_houses"
															type="number"
															className="form-control form-control-solid"
															disabled={isView}
														/>
													</div>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION F: REPORT UPLOAD */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section F: Report Upload
													</span>
												</h3>
											</div>
											<KTCardBody className="py-3">
												<div className="mb-10">
													<FileUpload
														label="Audit Report (PDF format)"
														value={values.report_file}
														disabled={isView}
														onChange={(val) =>
															setFieldValue("report_file", val)
														}
														onUpload={async (file) => {
															if (
																file &&
																file.type !== "application/pdf" &&
																!file.name.toLowerCase().endsWith(".pdf")
															) {
																toast.error(
																	"Only PDF format files are allowed!",
																);
																throw new Error(
																	"Only PDF format files are allowed!",
																);
															}
															if (file && file.size > 2 * 1024 * 1024) {
																toast.error("File size exceeds 2MB limit!");
																throw new Error("File size exceeds 2MB limit!");
															}
															return await uploadMutation.mutateAsync(file);
														}}
														accept="application/pdf,.pdf"
														maxSizeMB={2}
														description="Max 2MB (PDF format only)"
													/>
												</div>
											</KTCardBody>
										</KTCard>

										{/* SECTION G: PARA PARTICULARS */}
										<KTCard className="mb-5 mb-xl-8">
											<div className="card-header border-0 pt-5">
												<h3 className="card-title align-items-start flex-column">
													<span className="card-label fw-bold fs-3 mb-1">
														Section G: Para Particulars
													</span>
												</h3>
												<div className="card-toolbar">
													{!isView && (
														<button
															type="button"
															className="btn btn-sm btn-dark"
															onClick={() => {
																const districtName = districts?.find(
																	(d: any) =>
																		Number(d.id) === Number(values.district_id),
																)?.name;
																if (!districtName) {
																	toast.error(
																		"Select District in Section B before adding an Issue.",
																	);
																	return;
																}
																const issueNo = `PMAY-G-${districtName.toUpperCase()}-${values.issues.length + 1}`;
																const newIssue: PMAYGParaIssue = {
																	issue_no: issueNo,
																	issue_type_id: null,
																	issue_category_id: null,
																	issue_sub_category_id: null,
																	code_no: "",
																	description: "",
																	no_of_beneficiaries: null,
																	central_amount: null,
																	state_amount: null,
																	others_amount: null,
																	no_of_grievances: null,
																	hlc_reg_no: "",
																	status: "Pending",
																	recovery_amount: null,
																	hlc_recovery_amount: null,
																};
																setFieldValue("issues", [
																	...values.issues,
																	newIssue,
																]);
															}}
														>
															<KTIcon iconName="plus" className="fs-3" /> Add
															Issue
														</button>
													)}
												</div>
											</div>
											<KTCardBody className="py-3">
												{values.issues.map((issue: any, index: number) => {
													return (
														<PMAYGParaIssueRow
															key={index}
															index={index}
															isView={isView}
														isEdit={isEdit}
															isAdmin={userRoleId === 1}
															issue={issue}
															setFieldValue={setFieldValue}
															schemeId={selectedSchemeId}
															hlcOptions={hlcRegNumbers}
															remove={() => {
																const newList = values.issues.filter(
																	(_: any, i: number) => i !== index,
																);
																setFieldValue("issues", newList);
															}}
														/>
													);
												})}
											</KTCardBody>
										</KTCard>

										<div className="d-flex justify-content-end mb-10">
											<button
												type="button"
												className="btn btn-light me-3"
												onClick={() => navigate("/scheme-data-entry")}
											>
												Cancel
											</button>
											{!isView && (
												<button
													type="submit"
													className="btn btn-primary"
													disabled={
														submitMutation.isPending || updateMutation.isPending
													}
												>
													{submitMutation.isPending || updateMutation.isPending
														? "Submitting..."
														: isEdit
															? "Update PMAY-G Report"
															: "Submit PMAY-G Report"}
												</button>
											)}
										</div>
									</Form>
									);
									}}
							</Formik>
						</>
					)}

					{/* OTHER SCHEMES - Under Construction */}
					{![1, 2].includes(selectedSchemeId!) && !isNmpSelected && !isCmbfsSelected && !isVbGramGSelected && !isBlockDistrictAssemblySelected && !isHlcSelected && selectedSchemeId && (
						<div className="card shadow-sm mb-5">
							{selectedSubSchemeId && (
								<div className="card-header">
									<h3 className="card-title text-gray-800 fw-bold">
										{
											subSchemes.find((s) => s.id === selectedSubSchemeId)
												?.name
										}
									</h3>
									<div className="card-toolbar">
										<button
											type="button"
											onClick={() =>
												id
													? navigate("/scheme-data-entry")
													: setSelectedSubSchemeId(null)
											}
											className="btn btn-sm btn-light-primary"
										>
											<KTIcon iconName="arrow-left" className="fs-3" />
											{id ? "Back to List" : "Back to Sub-scheme List"}
										</button>
									</div>
								</div>
							)}
							{selectedSubSchemeId ? (
								<div className="card-body">
									<DsjeSubSchemeForm
										subSchemeId={selectedSubSchemeId}
										subSchemeName={
											subSchemes.find((s) => s.id === selectedSubSchemeId)
												?.name || ""
										}
										schemeId={selectedSchemeId}
										id={id}
										initialData={initialData}
										isView={isView}
										currentUser={currentUser}
										districts={districts}
										onBackToSubSchemeList={() => setSelectedSubSchemeId(null)}
									/>
								</div>
							) : (
								<div className="card-body text-center py-10">
									<KTIcon
										iconName="setting-2"
										className="fs-5x text-warning mb-5"
									/>
									<h3 className="fw-bold fs-2 text-gray-800">
										Under Construction
									</h3>
									<p className="text-muted">
										The data entry form for this scheme is currently being
										developed. Please check back later.
									</p>
								</div>
							)}
						</div>
					)}

					{/* NMP SCHEME */}
					{isNmpSelected && (
						<NMPSchemeStepper
							isView={isView}
							districts={districts}
							schemeId={selectedSchemeId || undefined}
							id={id}
							initialData={{
								...(initialData || {}),
								brp_district_id:
									initialData?.brp_district_id ||
									(currentUser?.district_id
										? Number(currentUser.district_id)
										: ""),
								brp_block_id:
									initialData?.brp_block_id ||
									(currentUser?.block_id ? Number(currentUser.block_id) : ""),
								brp_employee_code:
									initialData?.brp_employee_code ||
									currentUser?.employee_code ||
									"",
								brp_name: initialData?.brp_name || currentUser?.name || "",
							}}
						/>
					)}

					{/* CMBFS SCHEME */}
					{isCmbfsSelected && (
						<CMBFSSchemeStepper
							isView={isView}
							districts={districts}
							schemeId={selectedSchemeId || undefined}
							id={id}
							initialData={{
								...(initialData || {}),
								brp_district_id:
									initialData?.brp_district_id ||
									(currentUser?.district_id
										? Number(currentUser.district_id)
										: ""),
								brp_block_id:
									initialData?.brp_block_id ||
									(currentUser?.block_id ? Number(currentUser.block_id) : ""),
								brp_employee_code:
									initialData?.brp_employee_code ||
									currentUser?.employee_code ||
									"",
								brp_name: initialData?.brp_name || currentUser?.name || "",
							}}
						/>
					)}

					{/* BLOCK/DISTRICT ASSEMBLY SCHEME */}
					{isBlockDistrictAssemblySelected && (
						<BlockDistrictAssemblyForm
							isView={isView}
							districts={districts}
							schemeId={selectedSchemeId || undefined}
							id={id}
							initialData={initialData}
						/>
					)}

					{/* HLC SCHEME */}
					{isHlcSelected && (
						<HLCForm
							isView={isView}
							districts={districts}
							schemeId={selectedSchemeId || undefined}
							id={id}
							initialData={initialData}
						/>
					)}
				</>
			)}
		</>
	);
};

export default SchemeDataEntryFormPage;
